import"./index-ts7drhwc.mjs";
import {
  CodingAgentIcon,
  EditorIcon,
  FinderIcon,
  TerminalIcon,
  useSyncExternalStore,
  getBrowserStudioOperations,
  DEFAULT_PROPS_PATH_CLASSNAME,
  DEFAULT_PROPS_PATH_ACTIVE_CLASSNAME,
  expandDefaultPropsEditorRef,
  restartStudio2,
  callUpdateDefaultPropsApi,
  getOriginalLocationFromStack
} from "./index-hbzdkgeb.mjs";
import {
  deleteStaticFile2,
  getStaticFiles2,
  pause2,
  play2,
  shutDownStudio2,
  extractEnumJsonPaths,
  hasResolvedStack,
  getCachedResolvedStack,
  resolvedStackToSymbolicated,
  seek2,
  visualControlStore,
  visualControlRef,
  watchPublicFolder2,
  writeStaticFile2
} from "./index-212b6p4m.mjs";

// src/api/create-composition.tsx
import { Composition, Still } from "remotion";
import { jsx } from "react/jsx-runtime";
var createComposition = ({
  ...other
}) => () => {
  return /* @__PURE__ */ jsx(Composition, {
    ...other
  });
};
var createStill = ({
  ...other
}) => () => {
  return /* @__PURE__ */ jsx(Still, {
    ...other
  });
};
// src/api/focus-default-props-path.ts
var focusDefaultPropsPath = ({
  path,
  scrollBehavior
}) => {
  const currentlyActive = document.querySelector(`.${DEFAULT_PROPS_PATH_ACTIVE_CLASSNAME}`);
  if (currentlyActive !== null) {
    currentlyActive.classList.remove(DEFAULT_PROPS_PATH_ACTIVE_CLASSNAME);
  }
  const query = document.querySelector(`.${DEFAULT_PROPS_PATH_CLASSNAME}[data-json-path="${path.join(".")}"]`);
  if (query === null) {
    return {
      success: false
    };
  }
  const scrollToField = () => query.scrollIntoView({ behavior: scrollBehavior });
  if (expandDefaultPropsEditorRef.current === null) {
    scrollToField();
  } else {
    expandDefaultPropsEditorRef.current(scrollToField);
  }
  query.classList.add(DEFAULT_PROPS_PATH_ACTIVE_CLASSNAME);
  return {
    success: true
  };
};
// src/api/go-to-composition.ts
import { Internals } from "remotion";
var goToComposition = (compositionId) => {
  Internals.compositionSelectorRef.current?.selectComposition(compositionId);
};
// src/api/helpers/calc-new-props.ts
import { Internals as Internals2, getRemotionEnvironment } from "remotion";
var calcNewProps = (compositionId, defaultProps) => {
  if (!getRemotionEnvironment().isStudio) {
    throw new Error("saveDefaultProps can only be called in the Remotion Studio.");
  }
  const { compositionsRef } = Internals2;
  const compositionsStore = compositionsRef.current;
  if (!compositionsStore) {
    throw new Error("No compositions ref found. Are you in the Remotion Studio and are the Remotion versions aligned?");
  }
  const compositions = compositionsStore.getCompositions();
  const composition = compositions.find((c) => c.id === compositionId);
  if (!composition) {
    throw new Error(`No composition with the ID ${compositionId} found. Available compositions: ${compositions.map((c) => c.id).join(", ")}`);
  }
  const savedDefaultProps = composition.defaultProps ?? {};
  const generatedDefaultProps = defaultProps({
    schema: composition.schema,
    savedDefaultProps,
    unsavedDefaultProps: savedDefaultProps
  });
  return {
    composition,
    generatedDefaultProps
  };
};
// src/api/reevaluate-composition.ts
import { Internals as Internals3 } from "remotion";
var reevaluateComposition = () => {
  Internals3.resolveCompositionsRef.current?.reloadCurrentlySelectedComposition();
};
// src/api/save-default-props.ts
import { getRemotionEnvironment as getRemotionEnvironment2 } from "remotion";
var saveDefaultProps = async ({
  compositionId,
  defaultProps
}) => {
  if (!getRemotionEnvironment2().isStudio) {
    throw new Error("saveDefaultProps() is only available in the Studio");
  }
  if (window.remotion_isReadOnlyStudio) {
    throw new Error("saveDefaultProps() is not available in read-only Studio");
  }
  try {
    await import("zod");
  } catch {
    throw new Error('"zod" is required to use saveDefaultProps(), but is not installed.');
  }
  const z = await import("zod");
  let zodTypes = null;
  try {
    zodTypes = await import("@remotion/zod-types");
  } catch {}
  const { generatedDefaultProps, composition } = calcNewProps(compositionId, defaultProps);
  const registrationLocation = composition.stack === null || hasResolvedStack(composition.stack) ? getCachedResolvedStack(composition.stack) : await getOriginalLocationFromStack(composition.stack, "sequence").catch((error) => {
    console.warn(`saveDefaultProps(): Could not determine which file registers "${compositionId}", falling back to the root file`, error);
    return null;
  });
  const res = await callUpdateDefaultPropsApi(compositionId, generatedDefaultProps, composition.schema ? extractEnumJsonPaths({
    schema: composition.schema,
    zodRuntime: z,
    currentPath: [],
    zodTypes
  }) : [], resolvedStackToSymbolicated(registrationLocation));
  if (res.success) {
    return Promise.resolve();
  }
  const err = new Error(res.reason);
  err.stack = res.stack;
  return Promise.reject(err);
};
// src/api/toggle.ts
import { Internals as Internals4 } from "remotion";
var toggle = (e) => {
  Internals4.timeValueRef.current?.toggle(e);
};
// src/api/update-default-props.ts
var updateDefaultProps = saveDefaultProps;
// src/api/visual-control.ts
import { getRemotionEnvironment as getRemotionEnvironment3 } from "remotion";
var visualControl = (key, value, schema) => {
  useSyncExternalStore(visualControlStore.subscribe, visualControlStore.getSnapshot, visualControlStore.getSnapshot);
  if (getRemotionEnvironment3().isRendering) {
    return value;
  }
  if (!visualControlRef.current) {
    return value;
  }
  return visualControlRef.current.globalVisualControl(key, value, schema);
};
// src/api/watch-static-file.ts
import { getRemotionEnvironment as getRemotionEnvironment4 } from "remotion";
var watchStaticFile = (fileName, callback) => {
  if (!getRemotionEnvironment4().isStudio) {
    console.warn("watchStaticFile() is only available while using the Remotion Studio.");
    return { cancel: () => {
      return;
    } };
  }
  if (window.remotion_isReadOnlyStudio && !getBrowserStudioOperations()) {
    console.warn("watchStaticFile() is only available in an interactive Studio.");
    return { cancel: () => {
      return;
    } };
  }
  const withoutStaticBase = fileName.startsWith(window.remotion_staticBase) ? fileName.replace(window.remotion_staticBase, "") : fileName;
  const withoutLeadingSlash = withoutStaticBase.startsWith("/") ? withoutStaticBase.slice(1) : withoutStaticBase;
  let prevFileData = window.remotion_staticFiles.find((file) => file.name === withoutLeadingSlash);
  const { cancel } = watchPublicFolder2((staticFiles) => {
    const newFileData = staticFiles.find((file) => file.name === withoutLeadingSlash);
    if (!newFileData) {
      if (prevFileData !== undefined) {
        callback(null);
      }
      prevFileData = undefined;
      return;
    }
    if (prevFileData === undefined || prevFileData.lastModified !== newFileData.lastModified) {
      callback(newFileData);
      prevFileData = newFileData;
    }
  });
  return { cancel };
};

// src/index.ts
var StudioInternals = {
  CodingAgentIcon,
  EditorIcon,
  FinderIcon,
  TerminalIcon,
  createComposition,
  createStill
};
export {
  StudioInternals,
  deleteStaticFile2 as deleteStaticFile,
  focusDefaultPropsPath,
  getStaticFiles2 as getStaticFiles,
  goToComposition,
  pause2 as pause,
  play2 as play,
  reevaluateComposition,
  restartStudio2 as restartStudio,
  saveDefaultProps,
  seek2 as seek,
  shutDownStudio2 as shutDownStudio,
  toggle,
  updateDefaultProps,
  visualControl,
  watchPublicFolder2 as watchPublicFolder,
  watchStaticFile,
  writeStaticFile2 as writeStaticFile
};
